import React, { useEffect, useMemo, useState } from 'react';
import styles from './MfTargetPicker.module.css';
import useTheme from 'config/useTheme';

/**
 * Izbor onoga što u izvoru postoji — tabela, kolekcija, prefiksa ključeva.
 *
 * Ne zna ni za jedan izvor posebno. Spisak stiže sa servera (`/targets`), a
 * `kind` i `rows` se samo prikazuju: za MySQL je to `table`, za Redis tip
 * ključa (`hash`, `string`, `mixed`). Tip je važan jer odlučuje kakav će red
 * biti — hash daje red sa kolonama, string red sa dve kolone.
 *
 * Brojevi su procena i tako su i označeni („~"). Tačan broj bi na velikoj bazi
 * tražio pun prolaz kroz svaku tabelu samo da bi se nacrtao spisak.
 *
 * @param {object} props
 * @param {Array<{name: string, kind?: string, rows?: number, approximate?: boolean}>} props.targets
 * @param {Array<string>} props.initial   Već izabrano, da otvaranje ne izgubi izbor
 * @param {string} [props.note]           Napomena servera (npr. da je spisak iz uzorka)
 * @param {(names: Array<string>) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const MfTargetPicker = ({ targets = [], initial = [], note, onConfirm, onCancel }) => {
    const { dark } = useTheme();
    const [chosen, setChosen] = useState(() => new Set(initial));
    const [filter, setFilter] = useState('');

    // Escape zatvara. Dijalog pokriva formu, pa mora da postoji izlaz koji ne
    // traži da se pogodi dugme.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel]);

    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return q ? targets.filter((t) => t.name.toLowerCase().includes(q)) : targets;
    }, [targets, filter]);

    const toggle = (name) => setChosen((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        return next;
    });

    // „Sve" se odnosi na ono što je VIDLJIVO, dakle na filtrirano — inače bi
    // klik posle pretrage izabrao i ono što korisnik u tom trenutku ne vidi.
    const allVisibleChosen = visible.length > 0 && visible.every((t) => chosen.has(t.name));
    const toggleAll = () => setChosen((prev) => {
        const next = new Set(prev);
        for (const t of visible) {
            if (allVisibleChosen) next.delete(t.name);
            else next.add(t.name);
        }
        return next;
    });

    // Redosled izbora ne prati redosled spiska, nego se drži spiska — tako je
    // popunjeno polje čitljivo i ponovljivo.
    const confirm = () => onConfirm(targets.filter((t) => chosen.has(t.name)).map((t) => t.name));

    return (
        <div className={styles.backdrop} onClick={onCancel}>
            <div
                className={`${styles.panel} ${dark ? styles.dark : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.head}>
                    <span className={styles.title}>Select what to import</span>
                    <button type="button" className={styles.close} onClick={onCancel}>×</button>
                </div>

                {targets.length > 8 && (
                    <input
                        className={styles.filter}
                        placeholder="Filter…"
                        value={filter}
                        autoFocus
                        onChange={(e) => setFilter(e.target.value)}
                    />
                )}

                {note && <p className={styles.note}>{note}</p>}

                {targets.length === 0 ? (
                    <p className={styles.empty}>Nothing found in this source.</p>
                ) : (
                    <>
                        <label className={`${styles.row} ${styles.all}`}>
                            <input type="checkbox" checked={allVisibleChosen} onChange={toggleAll} />
                            <span className={styles.name}>
                                {allVisibleChosen ? 'Clear all' : 'Select all'}
                            </span>
                        </label>

                        <div className={styles.list}>
                            {visible.map((t) => (
                                <label key={t.name} className={styles.row}>
                                    <input
                                        type="checkbox"
                                        checked={chosen.has(t.name)}
                                        onChange={() => toggle(t.name)}
                                    />
                                    <span className={styles.name}>{t.name}</span>
                                    {t.kind && <span className={styles.kind}>{t.kind}</span>}
                                    {t.rows !== undefined && (
                                        <span className={styles.rows}>
                                            {t.approximate ? '~' : ''}{t.rows}
                                        </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </>
                )}

                <div className={styles.foot}>
                    {/* Broj izabranih stoji uz dugme, jer je to jedina posledica
                        klika koja se ne vidi iz same forme. */}
                    <span className={styles.count}>
                        {chosen.size === 0 ? 'Nothing selected'
                            : chosen.size === 1 ? '1 selected'
                                : `${chosen.size} selected`}
                    </span>
                    <button type="button" className={styles.ghost} onClick={onCancel}>Cancel</button>
                    <button
                        type="button"
                        className={styles.primary}
                        disabled={chosen.size === 0}
                        onClick={confirm}
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MfTargetPicker;
